import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {
  ConfigService,
  DialogWidth,
  REF_MODEL_STORE,
  RefModelStoreService,
  SnackBarService,
  ThemePalette,
  retryWithBackoff
} from '@q9elements/ui-kit/common';
import {
  ConfirmModalDialogComponent,
  TwoFaRequiredDialogComponent
} from '@q9elements/ui-kit/components';
import {get} from 'lodash';
import {catchError, firstValueFrom, throwError} from 'rxjs';

import {RefModelService} from '../../services/ref-model.service';
import {AuthService} from '../services/auth.service';

const ERROR_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  NETWORK_ERROR: 0
};

const REJECTION_CODES = {
  ACCESS_DENIED: 'access_denied',
  REF_MODEL_NOT_FOUND: 'refModel.err.not_found',
  TEAM_SUSPEND: 'team.err.implementation.suspend',
  REF_MODEL_NODE_NOT_FOUND: 'refModelNode.err.not_found',
  ELEMENT_NOT_EXISTS: 'element_not_exists',
  TEAM_IS_NOT_PRO: 'team.err.is_not_pro',
  TEAM_INFO_IS_NOT_PRO: 'team.info.is_not_pro',
  REF_MODEL_NO_EDIT_RIGHTS: 'refmodel.err.no_edit_rights',
  USER_IN_TEAM_BLOCKED: 'user_in_team.blocked',
  USER_BLOCKED: 'user.err.blocked',
  SALESFORCE_UPGRADE_PACKAGE: 'salesforce.err.upgrade_package',
  ADOPTION_CENTER_NO_ACCESS: 'adoption_center.no_access',
  ORGANIZATION_NOT_FOUND: 'organization.not_found',
  USER_NO_ENOUGH_PERMISSION: 'user.err.no_have_enough_permission',
  USER_DELETED_FROM_SYSTEM: 'user.err.deleted_from_system',
  REF_MODEL_RIGHTS_NOT_EXIST: 'refmodel.err.rights.does_not_exist',
  USER_NO_PERMISSION_MANAGE_REF_MODEL: 'user_does_not_have_permission_manage_refmodel',
  MAP_RIGHTS_NOT_EXIST: 'map.err.rights.does_not_exist',
  TWO_FACTOR_ERROR: 'two_factor_required',
  CUSTOM_VIEW_NAME_DUPLICATE: 'custom_view.err.name_duplicate',
  CANNOT_UPDATE_SF_META: 'refModelNode.err.cannot_update_sf_meta',
  CANNOT_UPDATE_SF_META_INSUFFICIENT_ACCESS:
    'refModelNode.err.cannot_update_sf_meta_insufficient_access',
  LICENSE_REQUIRED: 'license_required',
  EDITOR_SEATS_EXCEEDED: 'err.invite_editors_count_exceeded',
  EDITORS_SEATS_EXCEEDED: 'err.invite_more_editors_count_exceeded',
  SLACK_NO_CONNECTION: 'slack.err.no_connection'
};

@Injectable()
export class UnifiedErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private dialog: MatDialog,
    private rmeStore: RefModelStoreService,
    private snackBarService: SnackBarService,
    private authService: AuthService,
    private refModelService: RefModelService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    return next.handle(req).pipe(
      retryWithBackoff(),
      catchError((error: HttpErrorResponse) => {
        const parsedError = this.parseError(error);
        const ignoredErrors = [REJECTION_CODES.MAP_RIGHTS_NOT_EXIST];
        const componentHandledErrors = [
          REJECTION_CODES.EDITOR_SEATS_EXCEEDED,
          REJECTION_CODES.EDITORS_SEATS_EXCEEDED
        ];

        if (ignoredErrors.includes(parsedError.rejection)) {
          return;
        }

        if (componentHandledErrors.includes(parsedError.rejection)) {
          return throwError(() => error);
        }

        const handler =
          this.errorHandlers[parsedError.status]?.[parsedError.rejection] ||
          this.errorHandlers[parsedError.status]?.['default'];

        if (handler) {
          handler(parsedError.message, parsedError.status, parsedError.originalError);
        } else {
          this.handleGenericError(parsedError.message);
        }

        return throwError(() => error);
      })
    );
  }

  private parseError(error: HttpErrorResponse) {
    let errBody = error.error;

    if (errBody == null) {
      return {
        status: error.status,
        message: 'Unknown error',
        rejection: undefined,
        originalError: error
      };
    }

    if (typeof errBody === 'string') {
      try {
        errBody = JSON.parse(errBody);
      } catch {
        // Keep the original string if parsing fails (if error is not JSON)
      }
    }
    const message = typeof errBody === 'string' ? errBody : errBody?.error || 'Unhandled error';
    const rejection =
      typeof errBody === 'object' && (errBody as any).status != null
        ? String((errBody as any).status)
        : undefined;

    return {
      status: error.status,
      message,
      rejection,
      originalError: error
    };
  }

  private showErrorDialog(message: string) {
    this.dialog.closeAll();
    return this.dialog
      .open(ConfirmModalDialogComponent, {
        width: DialogWidth.SM,
        data: {
          modalTitle: 'POPUPS.ERROR',
          content: message,
          showCancelButton: false,
          confirmText: 'POPUPS.CONFIRM_BTN',
          color: ThemePalette.WARN
        }
      })
      .afterClosed();
  }

  private handleRefModelNodeNotFoundError(message: string): void {
    if (this.rmeStore.refModelId) {
      this.snackBarService.error(message);
      this.router.navigate(['/refmodel', this.rmeStore.refModelId]);
    } else {
      this.showErrorDialog(message);
      this.router.navigate(['/error']);
    }
  }

  private navigateToErrorPageWithDialog(message: string): void {
    this.showErrorDialog(message);
    this.router.navigate(['/error']);
  }

  private showSnackBarError(message: string): void {
    this.snackBarService.error(message);
  }

  private handleGenericError(message: string): void {
    this.showErrorDialog(message);
    this.router.navigate(['/error']);
  }

  private handleLicenseError(message: string): void {
    this.showErrorDialog(message).subscribe(() => window.location.reload());
  }

  private errorHandlers: {
    [statusCode: number]: {
      [rejectionCode: string]: (message: string, status: number, originalError?: any) => void;
    };
  } = {
    [ERROR_CODES.BAD_REQUEST]: {
      [REJECTION_CODES.ACCESS_DENIED]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.REF_MODEL_NOT_FOUND]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.TEAM_SUSPEND]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.REF_MODEL_NODE_NOT_FOUND]: msg => this.handleRefModelNodeNotFoundError(msg),
      [REJECTION_CODES.CUSTOM_VIEW_NAME_DUPLICATE]: msg => {
        return;
      },
      [REJECTION_CODES.SLACK_NO_CONNECTION]: () => {
        /* silent catch */
      },
      default: msg => this.showSnackBarError(msg)
    },
    [ERROR_CODES.UNAUTHORIZED]: {
      default: msg => {
        const redirectUrl = encodeURI(window.location.href);
        this.snackBarService.error('GENERAL.AUTH_ERROR');
        window.location.href =
          ConfigService.environment.MAIN_APP_URL + '/signin?redirect=' + redirectUrl;
      }
    },
    [ERROR_CODES.FORBIDDEN]: {
      [REJECTION_CODES.TEAM_IS_NOT_PRO]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.TWO_FACTOR_ERROR]: (msg, status, error) => {
        this.handleTwoFactorError(error.error);
      },
      [REJECTION_CODES.ELEMENT_NOT_EXISTS]: msg => {
        this.snackBarService.error(msg);
        if (this.rmeStore.refModelId) {
          this.router.navigate(['/refmodel', this.rmeStore.refModelId]);
        } else {
          this.navigateToErrorPageWithDialog(msg);
        }
      },
      [REJECTION_CODES.TEAM_INFO_IS_NOT_PRO]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.REF_MODEL_NO_EDIT_RIGHTS]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.USER_IN_TEAM_BLOCKED]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.USER_BLOCKED]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.SALESFORCE_UPGRADE_PACKAGE]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.ADOPTION_CENTER_NO_ACCESS]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.ORGANIZATION_NOT_FOUND]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.USER_NO_ENOUGH_PERMISSION]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.ACCESS_DENIED]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.USER_DELETED_FROM_SYSTEM]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.REF_MODEL_RIGHTS_NOT_EXIST]: msg => this.navigateToErrorPageWithDialog(msg),
      [REJECTION_CODES.REF_MODEL_NODE_NOT_FOUND]: msg => this.handleRefModelNodeNotFoundError(msg),
      [REJECTION_CODES.LICENSE_REQUIRED]: msg => this.handleLicenseError(msg),
      [REJECTION_CODES.CANNOT_UPDATE_SF_META]: msg => {
        return this.showSnackBarError('POPUPS.SALESFORCE_UPDATE_ERROR');
      },
      [REJECTION_CODES.CANNOT_UPDATE_SF_META_INSUFFICIENT_ACCESS]: msg => {
        return this.showSnackBarError('POPUPS.SALESFORCE_UPDATE_ERROR_INSUFFICIENT_ACCESS');
      },
      [REJECTION_CODES.USER_NO_PERMISSION_MANAGE_REF_MODEL]: msg => {
        this.snackBarService.error(msg);
        const refModelID = this.rmeStore.refModelId;
        if (refModelID) {
          firstValueFrom(this.authService.fetchUser(refModelID))
            .then(() => firstValueFrom(this.refModelService.getRefModelMeta(refModelID)))
            .then(refModel => {
              this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, refModel);
              this.rmeStore.set(REF_MODEL_STORE.ROOT_DATA, refModel);
            })
            .catch(innerError => {
              console.error('Error refreshing user/ref model data:', innerError);
              this.snackBarService.error('Failed to refresh user permissions or ref model data.');
            });
        } else {
          this.navigateToErrorPageWithDialog(msg);
        }
      },
      default: msg => this.showSnackBarError(msg)
    },
    [ERROR_CODES.NOT_FOUND]: {
      default: msg => this.navigateToErrorPageWithDialog(msg)
    },
    [ERROR_CODES.NETWORK_ERROR]: {
      default: () => {
        this.router.navigate(['/error']);
      }
    }
  };

  private showTwoFactorDialog(gracePeriodExpired = false) {
    return this.dialog
      .open(TwoFaRequiredDialogComponent, {
        width: DialogWidth.SM,
        data: {gracePeriodExpired}
      })
      .afterClosed();
  }

  private handleTwoFactorError(error): void {
    const gracePeriodExpired = get(error, 'error.gracePeriodExpired', false);
    const gracePeriodNotified = get(error, 'error.gracePeriodNotified', false);

    if (gracePeriodExpired || !gracePeriodNotified) {
      this.showTwoFactorDialog(gracePeriodExpired).subscribe(() => window.location.reload());
    }
  }
}

export function provideErrorInterceptor() {
  return {
    provide: HTTP_INTERCEPTORS,
    useClass: UnifiedErrorInterceptor,
    multi: true
  };
}
